import React, { useState } from 'react';
import { 
  Network, 
  ShieldCheck, 
  ShieldAlert, 
  AlertTriangle, 
  CheckCircle2, 
  Bot, 
  Terminal, 
  Layers,
  Info,
  Sliders,
  ArrowRight
} from 'lucide-react';

interface GraphNode {
  id: string;
  label: string;
  role: 'supervisor' | 'subagent' | 'tool' | 'gateway';
  type: string;
  decision: 'ALLOW' | 'REVIEW' | 'BLOCK';
  riskScore: number;
  latencyMs: number;
  reasons: string[];
  payload: Record<string, any>;
  x: number;
  y: number;
}

interface GraphEdge {
  from: string;
  to: string;
  status: 'ALLOW' | 'REVIEW' | 'BLOCK';
  label: string;
}

interface WorkflowPreset {
  id: string;
  name: string;
  description: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const PRESETS: WorkflowPreset[] = [
  {
    id: 'financial_delegation',
    name: 'Financial Invoice & Wire Delegation',
    description: 'Multi-agent supervisor delegating payment approval to worker agent and wire gateway.',
    nodes: [
      {
        id: 'sup_1',
        label: 'Financial Supervisor Agent',
        role: 'supervisor',
        type: 'Coordinator',
        decision: 'ALLOW',
        riskScore: 24,
        latencyMs: 14,
        reasons: ['Supervisory task decomposition authorized.'],
        payload: { task: 'Process Batch Invoices Q3', batch_id: 'batch-9901' },
        x: 30,
        y: 145
      },
      {
        id: 'sub_1',
        label: 'Invoice Extraction Worker',
        role: 'subagent',
        type: 'Autonomous Agent',
        decision: 'ALLOW',
        riskScore: 18,
        latencyMs: 38,
        reasons: ['Read-only PDF text extraction permitted under policy.'],
        payload: { invoice_id: 'INV-2048', amount: 45000.00, vendor: 'Acme Corp' },
        x: 440,
        y: 25
      },
      {
        id: 'sub_2',
        label: 'Vendor Verification Agent',
        role: 'subagent',
        type: 'Autonomous Agent',
        decision: 'REVIEW',
        riskScore: 68,
        latencyMs: 82,
        reasons: ['Vendor bank routing number changed within last 48 hours.', 'Requires dual-approval gate under NIST AI RMF.'],
        payload: { vendor_id: 'V-882', bank_account_changed: true, delta_hours: 12 },
        x: 440,
        y: 265
      },
      {
        id: 'gw_1',
        label: 'AgenticScale Gateway',
        role: 'gateway',
        type: 'Continuous Safety Layer',
        decision: 'REVIEW',
        riskScore: 78,
        latencyMs: 2,
        reasons: ['Dual-custody approval policy pol-fin-002 triggered.'],
        payload: { enforced_rules: ['pol-fin-001', 'pol-fin-002'] },
        x: 850,
        y: 145
      },
      {
        id: 'tool_1',
        label: 'Swift Wire Transfer API',
        role: 'tool',
        type: 'External High-Risk Tool',
        decision: 'BLOCK',
        riskScore: 92,
        latencyMs: 0,
        reasons: ['Execution halted pending human operator review.'],
        payload: { target: 'wire://swift.fedwire/transfer', amount: 45000.00 },
        x: 1260,
        y: 145
      }
    ],
    edges: [
      { from: 'sup_1', to: 'sub_1', status: 'ALLOW', label: 'delegate: extract_invoice' },
      { from: 'sup_1', to: 'sub_2', status: 'REVIEW', label: 'delegate: verify_vendor' },
      { from: 'sub_1', to: 'gw_1', status: 'ALLOW', label: 'pass data' },
      { from: 'sub_2', to: 'gw_1', status: 'REVIEW', label: 'flagged mutation' },
      { from: 'gw_1', to: 'tool_1', status: 'BLOCK', label: 'halt unapproved wire' }
    ]
  },
  {
    id: 'support_exfiltration',
    name: 'Customer Support PII Redaction Chain',
    description: 'Support conversational agent intercepted while attempting unredacted customer data export.',
    nodes: [
      {
        id: 'sup_2',
        label: 'Support Triage Router',
        role: 'supervisor',
        type: 'Router Agent',
        decision: 'ALLOW',
        riskScore: 12,
        latencyMs: 18,
        reasons: ['Classified customer ticket inquiry as Tier-2 Billing.'],
        payload: { ticket_id: 'TIC-8819', queue: 'billing' },
        x: 30,
        y: 145
      },
      {
        id: 'sub_3',
        label: 'CRM Database Agent',
        role: 'subagent',
        type: 'Data Retrieval Agent',
        decision: 'ALLOW',
        riskScore: 22,
        latencyMs: 44,
        reasons: ['Retrieved redacted customer profile.'],
        payload: { user_id: 'usr-4412', pii_masked: true },
        x: 440,
        y: 145
      },
      {
        id: 'gw_2',
        label: 'AgenticScale Gateway',
        role: 'gateway',
        type: 'DLP Guardrail',
        decision: 'BLOCK',
        riskScore: 94,
        latencyMs: 3,
        reasons: ['OWASP LLM06: Attempted bulk unredacted SSN/Credit Card export to external webhook.'],
        payload: { detected_pattern: 'UNMASKED_CREDIT_CARD', target: 'https://webhook.site/malicious-sink' },
        x: 850,
        y: 145
      },
      {
        id: 'tool_2',
        label: 'External Webhook Exporter',
        role: 'tool',
        type: 'Blocked Exfiltration Sink',
        decision: 'BLOCK',
        riskScore: 98,
        latencyMs: 0,
        reasons: ['Prevented outbound data leak at edge.'],
        payload: { destination: 'https://webhook.site/malicious-sink' },
        x: 1260,
        y: 145
      }
    ],
    edges: [
      { from: 'sup_2', to: 'sub_3', status: 'ALLOW', label: 'route inquiry' },
      { from: 'sub_3', to: 'gw_2', status: 'BLOCK', label: 'export_pii_bulk' },
      { from: 'gw_2', to: 'tool_2', status: 'BLOCK', label: 'interception blocked' }
    ]
  }
];

export const MultiAgentGraph: React.FC = () => {
  const [selectedPreset, setSelectedPreset] = useState<string>('financial_delegation');
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(PRESETS[0].nodes[2]);

  const currentPreset = PRESETS.find(p => p.id === selectedPreset) || PRESETS[0];

  const getNodeStyles = (decision: 'ALLOW' | 'REVIEW' | 'BLOCK') => {
    switch (decision) {
      case 'ALLOW':
        return {
          cardBg: 'bg-slate-900/95 border-emerald-500/60 shadow-[0_0_15px_rgba(16,185,129,0.15)]',
          badge: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
          roleText: 'text-emerald-400',
          riskText: 'text-emerald-400',
          accent: 'text-emerald-400'
        };
      case 'REVIEW':
        return {
          cardBg: 'bg-slate-900/95 border-amber-500/70 shadow-[0_0_15px_rgba(245,158,11,0.15)]',
          badge: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
          roleText: 'text-amber-400',
          riskText: 'text-amber-400',
          accent: 'text-amber-400'
        };
      case 'BLOCK':
        return {
          cardBg: 'bg-slate-900/95 border-rose-500/70 shadow-[0_0_15px_rgba(244,63,94,0.2)]',
          badge: 'bg-rose-500/20 text-rose-300 border border-rose-500/40',
          roleText: 'text-rose-400',
          riskText: 'text-rose-400',
          accent: 'text-rose-400'
        };
    }
  };

  const getEdgeStroke = (status: 'ALLOW' | 'REVIEW' | 'BLOCK') => {
    switch (status) {
      case 'ALLOW': return '#10B981';
      case 'REVIEW': return '#F59E0B';
      case 'BLOCK': return '#F43F5E';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Preset Selector & Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Network className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <span>Multi-Agent Delegation & Risk DAG</span>
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Visualize how intent, data, and policy boundaries propagate across coordinator agents and high-risk tool invocations.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                setSelectedPreset(preset.id);
                setSelectedNode(preset.nodes[0]);
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                selectedPreset === preset.id
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
              }`}
            >
              <span>{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Legend Bar */}
      <div className="flex items-center justify-between gap-3 text-xs bg-slate-100 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Security Posture Status:</span>
        <div className="flex items-center gap-4 text-xs font-semibold flex-wrap">
          <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-xs" />
            <span>ALLOW: Policy Approved</span>
          </span>
          <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-xs" />
            <span>REVIEW: Dual-Control Gate</span>
          </span>
          <span className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-xs" />
            <span>BLOCK: Zero-Trust Intercept</span>
          </span>
        </div>
      </div>

      {/* DAG Visualization Canvas Container (Scrollable on small screens, high-contrast dark canvas) */}
      <div className="relative rounded-2xl border border-slate-800 bg-[#070D18] overflow-x-auto shadow-2xl p-6 min-h-[440px]">
        {/* Background Engineering Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:32px_32px] opacity-25 pointer-events-none" />

        <div className="relative min-w-[1560px] h-[380px]">
          {/* SVG Connectors / Edges */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <defs>
              <marker id="arrow-allow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L0,8 L8,4 z" fill="#10B981" />
              </marker>
              <marker id="arrow-review" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L0,8 L8,4 z" fill="#F59E0B" />
              </marker>
              <marker id="arrow-block" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L0,8 L8,4 z" fill="#F43F5E" />
              </marker>
            </defs>
            {currentPreset.edges.map((edge, idx) => {
              const fromNode = currentPreset.nodes.find(n => n.id === edge.from);
              const toNode = currentPreset.nodes.find(n => n.id === edge.to);
              if (!fromNode || !toNode) return null;

              const nodeWidth = 230;
              const nodeHeight = 88;
              const startX = fromNode.x + nodeWidth;
              const startY = fromNode.y + (nodeHeight / 2);
              const endX = toNode.x;
              const endY = toNode.y + (nodeHeight / 2);
              const midX = (startX + endX) / 2;
              const midY = (startY + endY) / 2;

              const markerId = edge.status === 'ALLOW' ? 'url(#arrow-allow)' : edge.status === 'REVIEW' ? 'url(#arrow-review)' : 'url(#arrow-block)';

              return (
                <g key={idx}>
                  {/* Base Track */}
                  <path
                    d={`M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`}
                    fill="none"
                    stroke={getEdgeStroke(edge.status)}
                    strokeWidth="2.5"
                    strokeDasharray={edge.status === 'BLOCK' ? '6,4' : undefined}
                    strokeOpacity="0.4"
                  />
                  {/* Animated Flow Pulse */}
                  <path
                    d={`M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`}
                    fill="none"
                    stroke={getEdgeStroke(edge.status)}
                    strokeWidth="2.5"
                    className="animate-flow-dash"
                    markerEnd={markerId}
                  />
                  {/* Mid-edge label badge */}
                  <foreignObject x={midX - 75} y={midY - 14} width="150" height="28">
                    <div className="flex justify-center items-center h-full">
                      <span className={`text-[9.5px] px-2 py-0.5 rounded-full font-mono font-bold tracking-tight shadow-lg border backdrop-blur-md whitespace-nowrap ${
                        edge.status === 'ALLOW'
                          ? 'bg-emerald-950/95 text-emerald-300 border-emerald-700/80 shadow-emerald-950/60'
                          : edge.status === 'REVIEW'
                          ? 'bg-amber-950/95 text-amber-300 border-amber-700/80 shadow-amber-950/60'
                          : 'bg-rose-950/95 text-rose-300 border-rose-700/80 shadow-rose-950/60'
                      }`}>
                        {edge.label}
                      </span>
                    </div>
                  </foreignObject>
                </g>
              );
            })}
          </svg>

          {/* Node Elements */}
          <div className="relative z-10 w-full h-full pointer-events-auto">
            {currentPreset.nodes.map((node) => {
              const isSelected = selectedNode?.id === node.id;
              const styles = getNodeStyles(node.decision);

              return (
                <div
                  key={node.id}
                  onClick={() => setSelectedNode(node)}
                  style={{ left: `${node.x}px`, top: `${node.y}px` }}
                  className={`absolute w-[230px] p-3 rounded-xl border cursor-pointer transition-all duration-200 select-none shadow-xl ${
                    styles.cardBg
                  } ${
                    isSelected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-slate-950 scale-105 z-30' : 'hover:scale-102 z-10'
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 mb-1.5">
                    <span className={`text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1.5 ${styles.roleText}`}>
                      {node.role === 'supervisor' ? <Bot className="h-3.5 w-3.5" /> : node.role === 'tool' ? <Terminal className="h-3.5 w-3.5" /> : <Layers className="h-3.5 w-3.5" />}
                      <span>{node.type}</span>
                    </span>
                    <span className={`text-[9px] font-mono font-bold px-1.5 py-0.2 rounded-full ${styles.badge}`}>
                      {node.decision}
                    </span>
                  </div>

                  {/* Fully displayed label without truncation */}
                  <div className="font-bold text-xs text-white leading-snug">
                    {node.label}
                  </div>

                  <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-800 pt-1.5 font-mono">
                    <span>Risk: <strong className={styles.riskText}>{node.riskScore}/100</strong></span>
                    <span>{node.latencyMs}ms</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Node Inspector Drawer / Panel */}
      {selectedNode && (
        <div className="glass-panel p-5 sm:p-6 rounded-2xl space-y-4 animate-fade-in border border-slate-200 dark:border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl border ${
                selectedNode.decision === 'ALLOW'
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-600'
                  : selectedNode.decision === 'REVIEW'
                  ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-600'
                  : 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-600'
              }`}>
                {selectedNode.decision === 'ALLOW' ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : selectedNode.decision === 'REVIEW' ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <ShieldAlert className="h-5 w-5" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-base text-slate-900 dark:text-white">{selectedNode.label}</h3>
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                    selectedNode.decision === 'ALLOW'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
                      : selectedNode.decision === 'REVIEW'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                      : 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
                  }`}>
                    {selectedNode.decision}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-mono">
                  Type: <strong className="text-slate-800 dark:text-slate-200">{selectedNode.type}</strong> · Risk Score: <strong className="text-slate-800 dark:text-slate-200">{selectedNode.riskScore}/100</strong> · Latency: <strong className="text-slate-800 dark:text-slate-200">{selectedNode.latencyMs}ms</strong>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Info className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Inspecting Node Policy & Payload</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            {/* Policy Evaluation Rationale */}
            <div className="space-y-2">
              <span className="font-bold text-slate-900 dark:text-white block">Evaluated Safeguards & Rationale:</span>
              <div className="space-y-1.5">
                {selectedNode.reasons.map((reason, i) => (
                  <div key={i} className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="text-brand-600 dark:text-brand-400 font-bold">•</span>
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Inspected Payload Arguments */}
            <div className="space-y-2">
              <span className="font-bold text-slate-900 dark:text-white block">In-Flight Payload & Arguments:</span>
              <pre className="p-3 rounded-xl bg-slate-900 dark:bg-slate-950 text-emerald-400 font-mono text-[11px] leading-relaxed border border-slate-800 overflow-x-auto">
                {JSON.stringify(selectedNode.payload, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
